import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Search, Lightbulb, MessageSquare } from 'lucide-react';
import { EvidenceSearchResponse } from '../../services/evidenceService';
import { highlightTranscriptText, renderHighlightedText } from '../../utils/textHighlighter';

interface EvidenceResultModalProps {
  show: boolean;
  evidenceResult: EvidenceSearchResponse | null;
  selectedText: string;
  transcript?: string;
  onClose: () => void;
}

export const EvidenceResultModal: React.FC<EvidenceResultModalProps> = ({
  show,
  evidenceResult,
  selectedText,
  transcript,
  onClose
}) => {
  if (!show || !evidenceResult) return null;

  // Prepare highlighted transcript if we have matches and transcript
  const highlightedSegments = transcript && evidenceResult.highlight_info.transcript_matches.length > 0
    ? highlightTranscriptText(transcript, evidenceResult.highlight_info.transcript_matches)
    : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-2xl w-full max-h-[80vh] overflow-hidden relative z-[10000]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Search className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Evidence Analysis</h2>
                <p className="text-sm text-gray-500">Clinical significance and diagnostic relevance</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
            {/* Selected Text */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-500" />
                <h3 className="text-sm font-medium text-gray-700">Selected Text</h3>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-gray-800 italic">"{selectedText}"</p>
              </div>
            </div>

            {/* Clinical Significance */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-medium text-gray-700">Clinical Significance</h3>
              </div>
              <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {evidenceResult.evidence_explanation.clinical_significance}
                </p>
              </div>
            </div>

            {/* Diagnostic Relevance */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-green-500" />
                <h3 className="text-sm font-medium text-gray-700">Diagnostic Relevance</h3>
              </div>
              <div className="p-4 bg-green-50 border border-green-100 rounded-lg">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {evidenceResult.evidence_explanation.diagnostic_relevance}
                </p>
              </div>
            </div>

            {/* Full Highlighted Transcript */}
            {highlightedSegments && transcript && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-purple-500" />
                  <h3 className="text-sm font-medium text-gray-700">Full Transcript with Highlights</h3>
                </div>
                <div className="p-4 bg-gray-50 border border-gray-100 rounded-lg">
                  <div className="text-sm text-gray-800 font-mono leading-relaxed whitespace-pre-wrap">
                    {renderHighlightedText(highlightedSegments)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}; 